
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Building, CreditCard, MessageSquare, RefreshCw } from 'lucide-react';
import { useAdminStatistics } from '@/hooks/useAdminStatistics';
import AdminChartsSection from './AdminChartsSection';

const AdminDashboard = () => {
  const { statistics, loading, refetch } = useAdminStatistics();

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('ar-SA').format(num);
  };

  return (
    <div className="space-y-6">
      {/* Header with refresh button */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">لوحة الإحصائيات</h2>
        <Button 
          onClick={refetch} 
          disabled={loading}
          variant="outline"
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          تحديث البيانات
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-primary">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">إجمالي المستخدمين</CardTitle>
            <Users className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-900">
              {loading ? '...' : formatNumber(statistics?.totalUsers || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              {loading ? '...' : `${formatNumber(statistics?.totalLandlords || 0)} ملاك، ${formatNumber(statistics?.totalTenants || 0)} باحث`}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-success">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">العقارات النشطة</CardTitle>
            <Building className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-900">
              {loading ? '...' : formatNumber(statistics?.activeProperties || 0)}
            </div>
            <p className="text-xs text-muted-foreground">العقارات المتاحة</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-warning">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">المشتركين النشطين</CardTitle>
            <CreditCard className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-900">
              {loading ? '...' : formatNumber(statistics?.activeSubscriptions || 0)}
            </div>
            <p className="text-xs text-muted-foreground">الاشتراكات النشطة</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-info">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">الطلبات هذا الشهر</CardTitle>
            <MessageSquare className="h-4 w-4 text-info" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-900">
              {loading ? '...' : formatNumber(statistics?.monthlyRequests || 0)}
            </div>
            <p className="text-xs text-muted-foreground">طلبات إدارة العقار</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      {statistics && (
        <AdminChartsSection
          propertyTypeDistribution={statistics.propertyTypeDistribution}
          monthlyGrowthData={statistics.monthlyGrowthData}
          topLocations={statistics.topLocations}
          loading={loading}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
